import type { IGraphClient } from '../../src/services/GraphService';
import type { IQueueItem } from '../api/src/queue';
import { PRODUCT_NAME } from './app/product';

/** List URL segment; the title people see is set separately. */
export const REVIEW_LIST_URL_NAME: string = 'AIAdminFilingReview';
export const REVIEW_LIST_TITLE: string = 'Filing Review';

/** Where the app lives when nothing else says so (the background filer, in production). */
export const DEFAULT_APP_ORIGIN: string = 'https://administrator.eightythreelabs.com';

/**
 * Our columns. The file name goes in the list's own Title column (renamed "File name"), so there
 * is no empty Title column. QueueItemId is hidden: it only matches rows to queue items.
 */
const COLUMNS: object[] = [
  { name: 'WhyWaiting', displayName: 'Why it\'s waiting', text: { allowMultipleLines: true, linesForEditing: 3 } },
  { name: 'WaitingSince', displayName: 'Waiting since', dateTime: { format: 'dateTime' } },
  // Rich text so the link can be clicked from the list.
  { name: 'OpenInAIAdmin', displayName: `Open in ${PRODUCT_NAME}`, text: { allowMultipleLines: true, textType: 'richText', linesForEditing: 1 } },
  { name: 'QueueItemId', displayName: 'Queue item', text: {}, indexed: true, hidden: true }
];

interface IRowFields {
  Title?: string;
  WhyWaiting?: string;
  WaitingSince?: string;
  OpenInAIAdmin?: string;
  QueueItemId?: string;
}

interface IGraphPage<T> {
  value: T[];
  '@odata.nextLink'?: string;
}

function statusOf(error: unknown): number | undefined {
  return (error as { statusCode?: number } | undefined)?.statusCode;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** The same moment, whatever way SharePoint writes it back (it drops milliseconds). */
function sameTime(a: string | undefined, b: string | undefined): boolean {
  const x: number = Date.parse(a ?? '');
  const y: number = Date.parse(b ?? '');
  if (Number.isNaN(x) || Number.isNaN(y)) {
    return (a ?? '') === (b ?? '');
  }
  return Math.abs(x - y) < 1000;
}

/**
 * The review queue, mirrored to a SharePoint list in the library's site, so the office manager
 * can see what's waiting from inside SharePoint. One row per waiting document: its name, why it's
 * waiting, since when, and a link to the review queue in AI Admin.
 *
 * Only names and reasons are written, never what a document says. The rows belong to this list
 * alone: removing one never touches the document itself. The queue (our API) stays the source of
 * truth; this list just follows it, and a problem here never stops filing.
 */
export class ReviewList {
  public readonly reviewUrl: string;
  private _listId: string | undefined;
  private _ready: boolean = false;

  /**
   * @param appOrigin Where AI Admin is opened, e.g. https://administrator.eightythreelabs.com.
   * The browser passes its own address; the background filer passes the production one.
   */
  constructor(private readonly _graph: IGraphClient, private readonly _siteId: string, appOrigin: string) {
    this.reviewUrl = `${appOrigin.replace(/\/$/, '')}/#/review`;
  }

  /** Finds the list, or creates it the first time, and adds any columns it's missing. */
  public async ensure(): Promise<void> {
    if (this._ready) {
      return;
    }
    let listId: string | undefined = await this._findList();
    if (!listId) {
      const created: { id: string } = await this._graph.api(`/sites/${this._siteId}/lists`).post({
        displayName: REVIEW_LIST_URL_NAME, // Sets the URL name; the title is changed below.
        list: { template: 'genericList' },
        columns: COLUMNS
      });
      listId = created.id;
      // Cosmetic; the list works under either title.
      await this._graph.api(`/sites/${this._siteId}/lists/${listId}`).patch({ displayName: REVIEW_LIST_TITLE }).catch(() => undefined);
      await this._nameTitleColumn(listId).catch(() => undefined);
    } else {
      await this._ensureColumns(listId);
    }
    this._listId = listId;
    this._ready = true;
  }

  /**
   * Brings the list in step with the queue: adds a row for each waiting document that has none,
   * updates rows whose name or reasons changed, and removes rows for documents no longer waiting
   * (filed, left in Inbox, or removed from the queue). Every row is tried even if one fails;
   * then the first problem is thrown, for the caller to log.
   */
  public async sync(items: IQueueItem[]): Promise<void> {
    await this.ensure();
    const base: string = `/sites/${this._siteId}/lists/${this._listId}/items`;
    const rows: { id: string; fields: IRowFields }[] = await this._rows();
    const byQueueId: { [itemId: string]: { id: string; fields: IRowFields } } = {};
    const extra: string[] = [];
    for (const row of rows) {
      const key: string | undefined = row.fields.QueueItemId;
      if (!key || byQueueId[key]) {
        extra.push(row.id); // Not ours to match, or a second row for the same document.
      } else {
        byQueueId[key] = row;
      }
    }

    const problems: unknown[] = [];
    const attempt = async (step: () => Promise<unknown>): Promise<void> => {
      try {
        await step();
      } catch (e) {
        problems.push(e);
      }
    };

    const waiting: { [itemId: string]: boolean } = {};
    for (const q of items.filter(i => i.status === 'waiting')) {
      waiting[q.itemId] = true;
      const wanted: IRowFields = this._fieldsOf(q);
      const row: { id: string; fields: IRowFields } | undefined = byQueueId[q.itemId];
      if (!row) {
        await attempt(() => this._graph.api(base).post({ fields: wanted }));
      } else if (this._changed(row.fields, wanted)) {
        await attempt(() => this._graph.api(`${base}/${row.id}/fields`).patch(wanted));
      }
    }
    for (const key of Object.keys(byQueueId)) {
      if (!waiting[key]) {
        extra.push(byQueueId[key].id);
      }
    }
    for (const id of extra) {
      await attempt(() => this._graph.api(`${base}/${id}`).delete().catch((e: unknown) => {
        if (statusOf(e) !== 404) {
          throw e; // Already gone is fine.
        }
      }));
    }
    if (problems.length) {
      throw problems[0];
    }
  }

  private _fieldsOf(q: IQueueItem): IRowFields {
    return {
      Title: q.fileName,
      WhyWaiting: q.reasons.join(' ') || 'Needs a quick check.',
      WaitingSince: q.readAt,
      OpenInAIAdmin: `<a href="${escapeHtml(this.reviewUrl)}">Open in ${PRODUCT_NAME}</a>`,
      QueueItemId: q.itemId
    };
  }

  private _changed(have: IRowFields, want: IRowFields): boolean {
    // SharePoint wraps rich text in its own markup, so the link only has to still be there.
    return (have.Title ?? '') !== want.Title
      || (have.WhyWaiting ?? '') !== want.WhyWaiting
      || !sameTime(have.WaitingSince, want.WaitingSince)
      || (have.OpenInAIAdmin ?? '').indexOf(escapeHtml(this.reviewUrl)) === -1;
  }

  private async _rows(): Promise<{ id: string; fields: IRowFields }[]> {
    const rows: { id: string; fields: IRowFields }[] = [];
    let url: string | undefined = `/sites/${this._siteId}/lists/${this._listId}/items?$expand=fields($select=Title,WhyWaiting,WaitingSince,OpenInAIAdmin,QueueItemId)&$top=500`;
    while (url) {
      const page: IGraphPage<{ id: string; fields: IRowFields }> = await this._graph.api(url).get();
      rows.push(...page.value);
      url = page['@odata.nextLink'];
    }
    return rows;
  }

  private async _findList(): Promise<string | undefined> {
    // Graph accepts a list's title in place of its ID; the URL name covers a renamed list.
    for (const title of [REVIEW_LIST_TITLE, REVIEW_LIST_URL_NAME]) {
      try {
        const list: { id: string } = await this._graph.api(`/sites/${this._siteId}/lists/${encodeURIComponent(title)}?$select=id`).get();
        return list.id;
      } catch (error) {
        if (statusOf(error) !== 404) {
          throw error;
        }
      }
    }
    return undefined;
  }

  private async _ensureColumns(listId: string): Promise<void> {
    const existing: IGraphPage<{ name: string }> = await this._graph.api(`/sites/${this._siteId}/lists/${listId}/columns?$select=name`).get();
    const names: { [name: string]: boolean } = {};
    for (const c of existing.value) {
      names[c.name] = true;
    }
    for (const column of COLUMNS as { name: string }[]) {
      if (!names[column.name]) {
        await this._graph.api(`/sites/${this._siteId}/lists/${listId}/columns`).post(column);
      }
    }
  }

  /** Shows the list's Title column as "File name". */
  private async _nameTitleColumn(listId: string): Promise<void> {
    const columns: IGraphPage<{ id: string; name: string }> = await this._graph.api(`/sites/${this._siteId}/lists/${listId}/columns?$select=id,name`).get();
    const title: { id: string } | undefined = columns.value.filter(c => c.name === 'Title')[0];
    if (title) {
      await this._graph.api(`/sites/${this._siteId}/lists/${listId}/columns/${title.id}`).patch({ displayName: 'File name' });
    }
  }
}
